// Undo on the line Board activity keeps for an agent's write on a doc page (Features 7): the page put back to just before
// that write, while it is still the page's newest version. After that the line says to use Page history. The owner's
// alone: a shared view never gets it.

import { useEffect, useState } from "react";
import type { ID } from "../../shared/types";
import type { PageVersionInfo } from "../../shared/pageVersions";
import { api } from "../lib/api";
import { toast, useStore } from "../state/store";
import { UndoButton } from "./UndoButton";

const put = new Set<number>();

/** A page's versions, asked once for every line of the feed that is about it (and again when the page changes). */
const asked = new Map<string, { updated: number; read: Promise<PageVersionInfo[]> }>();
function versionsOf(page: ID, updated: number): Promise<PageVersionInfo[]> {
  const kept = asked.get(page);
  if (kept && kept.updated === updated) return kept.read;
  const read = api<{ versions: PageVersionInfo[] }>(`/api/pages/${encodeURIComponent(page)}/versions`).then((r) => r.versions);
  asked.set(page, { updated, read });
  read.catch(() => asked.delete(page));
  return read;
}

export function PageUndo({ to }: { to: { page: ID; version: number } }) {
  const updated = useStore((s) => s.entries[to.page]?.updatedAt ?? 0);
  const [now, setNow] = useState<"undo" | "since" | "back" | "gone" | null>(put.has(to.version) ? "back" : null);
  // where the write stands: still the page's newest version, or not
  useEffect(() => {
    if (put.has(to.version)) return;
    let live = true;
    versionsOf(to.page, updated)
      .then((list) => live && setNow(list[0]?.id === to.version ? "undo" : list.some((v) => v.id === to.version) ? "since" : "gone"))
      .catch(() => live && setNow("gone"));
    return () => {
      live = false;
    };
  }, [to.page, to.version, updated]);
  if (now === null || now === "gone") return null;
  if (now === "back") return <span className="fd-undone">Put back</span>;
  if (now === "since") return <span className="fd-undone">The page has changed since: use Page history</span>;
  return (
    <UndoButton
      tip="Puts the page back to just before this write"
      run={async () => {
        const { restoreVersion } = await import("../boards/doc/PageHistory");
        const r = await restoreVersion(to.page, to.version, true);
        if (!r) return;
        put.add(to.version);
        setNow("back");
        toast({ level: "success", title: "Put the page back", body: "It is as it was before that write. Page history has both.", key: `page-undo:${to.page}` });
      }}
    />
  );
}
